import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

/**
 * Verificação de saúde da criptografia
 * Testa se a chave master-encryption-key está configurada no Vault
 * 
 * PERFORMANCE OPTIMIZATION:
 * - Cache health check result in sessionStorage with 5-minute TTL
 * - Skip redundant checks on page refresh within TTL window
 */

let encryptionHealthy = false;
let healthCheckCompleted = false;

// Cache configuration
const HEALTH_CACHE_KEY = '_encryption_health_cache';
const HEALTH_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

interface HealthCacheEntry {
  timestamp: number;
  healthy: boolean;
  userId: string;
}

/**
 * Check if cached health result is still valid
 */
const getCachedHealthStatus = (userId: string): boolean | null => {
  try {
    const cached = sessionStorage.getItem(HEALTH_CACHE_KEY);
    if (!cached) return null;
    
    const entry: HealthCacheEntry = JSON.parse(cached);
    const isValid = 
      entry.userId === userId && 
      entry.healthy && 
      Date.now() - entry.timestamp < HEALTH_CACHE_TTL;
    
    if (isValid) {
      logger.info('[Encryption Health] ✅ Using cached health status (valid for', 
        Math.round((HEALTH_CACHE_TTL - (Date.now() - entry.timestamp)) / 1000), 'more seconds)');
      return entry.healthy;
    }
    
    return null;
  } catch {
    return null;
  }
};

/**
 * Save health check result to cache
 */
const setCachedHealthStatus = (userId: string, healthy: boolean): void => {
  try {
    const entry: HealthCacheEntry = {
      timestamp: Date.now(),
      healthy,
      userId
    };
    sessionStorage.setItem(HEALTH_CACHE_KEY, JSON.stringify(entry));
  } catch {
    // Ignore storage errors
  }
};

/**
 * Executa verificação de saúde da criptografia com retry automático
 * Retorna true se a criptografia está funcionando, false caso contrário
 */
export const checkEncryptionHealth = async (retries = 3): Promise<boolean> => {
  // Fast path: already checked in this session
  if (healthCheckCompleted && encryptionHealthy) {
    return encryptionHealthy;
  }

  // Verificar se o usuário está autenticado
  const { data: { session } } = await supabase.auth.getSession();
  
  if (!session?.access_token) {
    logger.warn('[Encryption Health] ⚠️  No authenticated user - skipping health check');
    encryptionHealthy = false;
    healthCheckCompleted = false;
    return false;
  }

  // Check sessionStorage cache first (PERFORMANCE OPTIMIZATION)
  const cachedStatus = getCachedHealthStatus(session.user.id);
  if (cachedStatus !== null) {
    encryptionHealthy = cachedStatus;
    healthCheckCompleted = true;
    return cachedStatus;
  }

  const startTime = performance.now();

  for (let attempt = 1; attempt <= retries; attempt++) {
    logger.info(`[Encryption Health] Tentativa ${attempt}/${retries}...`);

    try {
      // Testar criptografia com dados de teste
      const testData = 'health-check-test';
      
      // Adicionar timeout de 5 segundos
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Health check timeout after 5s')), 5000)
      );
      
      const encryptPromise = supabase.functions.invoke('encrypt-data', {
        body: { dataToEncrypt: testData },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });
      
      const { data: encryptData, error: encryptError } = await Promise.race([
        encryptPromise,
        timeoutPromise
      ]) as any;

      if (encryptError || !encryptData?.encrypted || !encryptData?.nonce) {
        logger.warn(`[Encryption Health] ❌ Encrypt failed (attempt ${attempt}/${retries})`);
        logger.warn('[Encryption Health] Error:', encryptError?.message || 'Invalid response');
        
        if (attempt < retries) {
          logger.info('[Encryption Health] Aguardando 1s antes de retry...');
          await new Promise(resolve => setTimeout(resolve, 1000));
          continue;
        }
        
        encryptionHealthy = false;
        healthCheckCompleted = true;
        return false;
      }

      // Testar descriptografia
      const decryptPromise = supabase.functions.invoke('decrypt-data', {
        body: { 
          encrypted: encryptData.encrypted,
          nonce: encryptData.nonce 
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });
      
      const { data: decryptData, error: decryptError } = await Promise.race([
        decryptPromise,
        timeoutPromise
      ]) as any;

      if (decryptError || !decryptData?.decrypted) {
        const errorMsg = decryptError?.message || '';
        const errorStatus = (decryptError as any)?.status;
        const isKeyMissing = errorStatus === 503 || 
                           errorMsg.includes('not configured') || 
                           errorMsg.includes('Service unavailable');
        
        if (isKeyMissing) {
          logger.error('[Encryption Health] ❌ MASTER_ENCRYPTION_KEY missing in Edge Functions');
          logger.error('[Encryption Health] 📋 To fix this:');
          logger.error('[Encryption Health] 1. Go to: https://supabase.com/dashboard/project/ojdktjavsdgcjavmrsyc/settings/functions');
          logger.error('[Encryption Health] 2. Add secret "MASTER_ENCRYPTION_KEY" with a strong key (32+ chars)');
          logger.error('[Encryption Health] 3. Reload the application');
        } else {
          logger.error('[Encryption Health] ❌ Edge Function runtime error');
          logger.error('[Encryption Health] Status:', errorStatus);
          logger.error('[Encryption Health] Message:', errorMsg);
          logger.error('[Encryption Health] Check function logs: https://supabase.com/dashboard/project/ojdktjavsdgcjavmrsyc/functions/decrypt-data/logs');
        }
        
        logger.warn('[Encryption Health] ⚠️  ENCRYPTION DISABLED - Application running without encryption');
        
        if (attempt < retries) {
          logger.info('[Encryption Health] Aguardando 1s antes de retry...');
          await new Promise(resolve => setTimeout(resolve, 1000));
          continue;
        }
        
        encryptionHealthy = false;
        healthCheckCompleted = true;
        return false;
      }

      // Verificar se a descriptografia retornou o valor correto
      if (decryptData.decrypted !== testData) {
        logger.error('[Encryption Health] ❌ Decryption returned incorrect data');
        logger.error('[Encryption Health] Expected:', testData);
        logger.error('[Encryption Health] Got:', decryptData.decrypted);
        
      if (attempt < retries) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        continue;
      }
        
        encryptionHealthy = false;
        healthCheckCompleted = true;
        return false;
      }

      const duration = performance.now() - startTime;
      logger.info(`[Encryption Health] ✅ Encryption is healthy and working correctly (${duration.toFixed(0)}ms)`);
      
      // Cache the successful result
      setCachedHealthStatus(session.user.id, true);
      
      encryptionHealthy = true;
      healthCheckCompleted = true;
      return true;

    } catch (error) {
      logger.error(`[Encryption Health] ❌ Exception on attempt ${attempt}/${retries}:`, error);
      
      if (attempt < retries) {
        logger.info('[Encryption Health] Aguardando 1s antes de retry...');
        await new Promise(resolve => setTimeout(resolve, 1000));
        continue;
      }
      
      logger.warn('[Encryption Health] ⚠️  ENCRYPTION DISABLED - Application running without encryption');
      encryptionHealthy = false;
      healthCheckCompleted = true;
      return false;
    }
  }

  return false;
};

/**
 * Retorna o status atual da saúde da criptografia
 */
export const isEncryptionHealthy = (): boolean => {
  return encryptionHealthy;
};

/**
 * Força uma nova verificação de saúde (útil para retry)
 * Also clears the cache to force a fresh check
 */
export const recheckEncryptionHealth = async (): Promise<boolean> => {
  healthCheckCompleted = false;
  try {
    sessionStorage.removeItem(HEALTH_CACHE_KEY);
  } catch {
    // Ignore storage errors
  }
  return checkEncryptionHealth();
};

/**
 * Clear the health check cache (useful on logout)
 */
export const clearHealthCache = (): void => {
  try {
    sessionStorage.removeItem(HEALTH_CACHE_KEY);
    healthCheckCompleted = false;
    encryptionHealthy = false;
  } catch {
    // Ignore storage errors
  }
};
